import { CheckCircle2, XCircle, AlertTriangle, Droplets, Package, Newspaper, Magnet, Battery } from "lucide-react";
import { t } from "@/lib/i18n";
import PageHeader from "@/components/PageHeader";
import FaqList from "@/components/FaqList";
import Reveal from "@/components/Reveal";
import { useSite } from "@/context/SiteContext";

const BINS = [
  { title: "Dry waste", tone: "bg-[var(--leaf)] text-white", icon: CheckCircle2, tag: "Give to your kabadiwala", items: ["Newspaper, books, copies", "Cardboard & cartons", "Plastic bottles, tubs, containers", "Metal — iron, steel, aluminium, copper, brass", "Glass bottles & jars", "Old electronics & cables"] },
  { title: "Wet waste", tone: "bg-[#8D6E3F] text-white", icon: XCircle, tag: "Compost or municipal collection", items: ["Vegetable & fruit peels", "Leftover food", "Tea leaves & coffee grounds", "Flowers & garden leaves", "Egg shells"] },
  { title: "Hazardous", tone: "bg-[var(--terracotta)] text-white", icon: AlertTriangle, tag: "Keep separate · special disposal", items: ["Batteries", "Expired medicines & syringes", "Paint, chemicals, pesticides", "Sanitary waste & diapers", "Tube lights & CFLs"] },
];
const PREP = [
  { icon: Droplets, title: "Rinse & dry", body: "Quickly rinse bottles, tins and containers so they don't smell or spoil other items." },
  { icon: Package, title: "Flatten boxes", body: "Flattened cardboard takes less space at home and on the kabadiwala's cart." },
  { icon: Newspaper, title: "Bundle paper", body: "Tie newspapers and books separately with string — they're easier to weigh." },
  { icon: Magnet, title: "Keep metals apart", body: "Different metals have very different rates. Keep copper and aluminium separate." },
  { icon: Battery, title: "Store e-waste safely", body: "Keep old phones, chargers and batteries in a box, away from heat and kids." },
];

export default function Learn() {
  const { content } = useSite();
  return (
    <div data-testid="learn-page">
      <PageHeader testid="learn-header" eyebrow="Learn" title={t("Sort smart. It takes two minutes a day.")}
        subtitle="Good segregation keeps your home cleaner, gets you a better price and helps more of your waste get recycled." />

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-3 gap-5">
        {BINS.map(({ title, tone, icon: Icon, tag, items }, i) => (
          <Reveal key={t(title)} delay={i * 100} className="bg-white rounded-3xl border border-[rgba(20,40,26,.08)] overflow-hidden card-lift" data-testid={`learn-bin-${i}`}>
            <div className={`${tone} px-6 py-5`}>
              <div className="flex items-center gap-2"><Icon size={20} /><h2 className="text-xl md:text-2xl font-bold">{t(title)}</h2></div>
              <div className="text-xs mt-1 opacity-85">{t(tag)}</div>
            </div>
            <ul className="p-6 space-y-2.5">
              {items.map((it) => <li key={t(it)} className="text-sm text-[var(--ink-soft)] flex gap-2.5"><span className="w-1.5 h-1.5 rounded-full bg-[var(--marigold)] mt-2 shrink-0" />{t(it)}</li>)}
            </ul>
          </Reveal>
        ))}
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 grid lg:grid-cols-12 gap-10 items-center">
        <Reveal className="lg:col-span-5 order-2 lg:order-1">
          <img src={content.image_home} alt={t("A family sorting dry waste at home")} className="rounded-[32px] w-full object-cover aspect-[4/3]" />
        </Reveal>
        <div className="lg:col-span-7 order-1 lg:order-2">
          <Reveal>
            <div className="text-xs font-bold tracking-[0.2em] uppercase text-[var(--leaf)]">{t("Before your pickup")}</div>
            <h2 className="mt-3 text-3xl sm:text-4xl font-bold">{t("Five quick habits that help.")}</h2>
          </Reveal>
          <div className="mt-8 space-y-3">
            {PREP.map(({ icon: Icon, title, body }, i) => (
              <Reveal key={t(title)} delay={i * 70} className="flex gap-4 p-4 rounded-2xl bg-white border border-[rgba(20,40,26,.08)]" data-testid={`learn-prep-${i}`}>
                <span className="w-11 h-11 rounded-xl bg-[var(--marigold-soft)] text-[#7A4B07] flex items-center justify-center shrink-0"><Icon size={20} /></span>
                <div><h3 className="font-bold">{t(title)}</h3><p className="text-sm text-[var(--ink-soft)] mt-1">{t(body)}</p></div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-8" id="faq" data-testid="learn-faq-section">
        <Reveal className="text-center">
          <div className="text-xs font-bold tracking-[0.2em] uppercase text-[var(--leaf)]">{t("FAQ")}</div>
          <h2 className="mt-3 text-3xl sm:text-4xl font-bold">{t("Frequently asked questions")}</h2>
        </Reveal>
        <div className="mt-10"><FaqList /></div>
      </section>
    </div>
  );
}
